import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import './Select.css';
import { useTranslation } from 'utils/modifiedTranslation';
import { randomString } from 'new-ui/functions';
import { isArray, isString } from 'lodash';
import app from 'new-ui/app';
import ArrowDown from 'assets/icons/arrow-circle-black.svg';
import XIcon from 'assets/icons/x.svg';
import addIcon from 'assets/icons/AddIcon.svg';
import { Tag } from 'new-ui/Components/Tags';
import { INTAKE_GA } from 'modules/ga/intakeEvents';

const isValueNotEmpty = (value) => !!value || (typeof value === 'number' && value === 0);

const isOptionSelected = (value, option) => {
  if (!isValueNotEmpty(value)) {
    return false;
  }
  if (isArray(value)) {
    return value.includes(option.value);
  }
  if (isString(value) || typeof value === 'number') {
    // e.g. ecog for "old" diseases is number
    return String(option.value) === String(value);
  }
  return false;
};

const HCPSelect = (props) => {
  const {
    renderOnSelect = true,
    disabled = false,
    showAutofill = true,
    onselect,
    initValue,
    options,
    icon,
    isMulti = false,
    useTags = false,
    placeholder,
    className,
    defaultOpen = false,
  } = props;

  const [value, setValue] = useState(initValue);
  const [showTags, setShowTags] = useState(!!useTags && isValueNotEmpty(initValue));
  const { t } = useTranslation();

  const $select = useRef();

  const [showOptions, setShowOptions] = useState(defaultOpen);

  // This useEffect will add an event listener for clicks outside the select box
  useEffect(() => {
    const handleClickOutside = (event) => {
      if ($select.current && !$select.current.contains(event.target)) {
        setShowOptions(false); // Close dropdown if click is outside of the component
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [$select]);

  useEffect(()=>{
    const $current = $select?.current;
    if ($current) {
      const $title = $current.querySelector('.hcp-select-title');
      $title.innerHTML = getTitle();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const getOptionTitle = (option) => {
    return option.title || option.label;
  };

  const clear = (ev) => {
    ev.stopPropagation();
    setValue(null);
    setShowOptions(false);
    onselect(null);
  };

  const getTitle = () => {
    const selecteds = options.filter((o) => isOptionSelected(value, o));
    if (!selecteds?.length) return placeholder || 'Please select...';
    if (isMulti) {
      return selecteds.map((s)=>{
        return s.title || s.label;
      }).join(', ');
    }

    return selecteds[0].title || selecteds[0].label;
  };

  const callOnSelect = (v) => {
    onselect(v);
    // if more than 1 Intake component is rendered can be a bug
    // it is better to relay on window._accordions[id]
    if (renderOnSelect) window._accordion.render();
  };

  return (
    <>
      {(useTags && isMulti && Array.isArray(value) && showTags) ? (
        <div className="hcp-tags select-tags-box">
          { value.map((v, i)=>{
            const o = options.find((option) => isOptionSelected(v, option));
            // eslint-disable-next-line
            if (!o) return;
            return (
              <Tag
                key={i}
                title={o.title || o.label}
                value={v}
                action={(v)=>{
                  const newValue = [...value];
                  newValue.splice(newValue.indexOf(v), 1);
                  setValue(newValue);
                  onselect(newValue);
                  if (newValue.length) return;
                  setShowTags(false);
                }}
              />
            );
          }) }
          <div
            onClick={()=>{
              setShowTags(false);
              setTimeout(()=>{
                if (showAutofill) {
                  const target = $select.current.querySelector('input');
                  if (target) {
                    target.focus();
                  }
                }
              }, 10);
              setShowOptions(true);
              app.sendGoogleAnalyticsEvent(INTAKE_GA.category, INTAKE_GA.events.treatment_q_add_more_clicked);
            }}
            className="add-tag"
          >
            <img src={addIcon} alt="add icon" />
            <span
              className="select-add-tag"
            >
              {t('search.search_biomarkers_tag_selector_button')}
            </span>
          </div>
        </div>
      ) : null }
      {
        !showTags
          ? (
            <div
              ref={$select}
              key={randomString()}
              className={cx('hcp-select', className, {
                'hcp-select-visible': showOptions,
                'hcp-select-value': isValueNotEmpty(value),
                'hcp-select-disabled': disabled,
              })}
            >
              <div
                className="hcp-select-header"
                onClick={() => {
                  const s = !showOptions;
                  if (s) {
                    setTimeout(()=>{
                      if (showAutofill) {
                        const target = $select.current.querySelector('input');
                        if (target) {
                          target.focus();
                        }
                      }
                    }, 10);
                  }
                  setShowOptions(s);
                }}
              >
                <div className="hcp-select-title">{getTitle()}</div>
                <div className="hcp-select-icon hcp-select-icon-hide" onClick={clear}>
                  <img src={XIcon} alt="" />
                </div>
                <div className="hcp-select-icon hcp-select-icon-main">
                  {
                    icon ? <img src={icon} alt="" /> : <img className="hcp-select-icon-down" src={ArrowDown} alt="" />
                  }
                </div>
              </div>
              {showOptions && (
                <div className="hcp-select-body">
                  {
                    showAutofill ? (
                      <div className="hcp-select-input">
                        <input
                          onKeyUp={(ev) => {
                            const v = ev.currentTarget.value;
                            const $options = [...$select.current.querySelectorAll('.hcp-select-option')];
                            $options.forEach(($option) => {
                              const $title = $option.querySelector('.hcp-select-option-title');
                              if ($title.innerHTML.toLowerCase().indexOf(v.toLowerCase()) === -1) $option.classList.add('hidden');
                              else $option.classList.remove('hidden');
                            });
                          }}
                          placeholder={t('general.start_typing')}
                        />
                      </div>
                    ) : null
                  }
                  <div className="hcp-select-clear hcp-select-option" onClick={clear}>
                    <div className="hcp-select-option-title">
                      {t('general.clear_selection')}
                    </div>
                  </div>
                  <div className="hcp-select-options">
                    {options.map((option) => {
                      return (
                        <div
                          key={randomString()}
                          className={cx(
                            'hcp-select-option',
                            { 'hcp-select-option-selected': isOptionSelected(value, option) },
                          )}
                          onClick={() => {
                            if (isMulti) {
                            // Multi-select logic here
                              if (!value) {
                              // Initialize value as an array if it's currently null or undefined
                                const _newValue = [option.value];
                                setValue(_newValue);
                                callOnSelect(_newValue); // Pass the updated array to the onselect callback
                              } else if (Array.isArray(value)) {
                                const isSelected = value.includes(option.value);
                                let updatedValue;
                                if (isSelected) {
                                  updatedValue = value.filter((v) => v !== option.value);
                                } else {
                                  updatedValue = [...value, option.value];
                                }
                                setValue(updatedValue);
                                callOnSelect(updatedValue); // Pass the updated array to the onselect callback
                              } else if (isString(value)) setValue([value]);
                              setShowOptions(false); // Close the dropdown after selection
                              if (useTags) setShowTags(true);
                            } else {
                            // Single-select logic
                              setValue(option.value);
                              callOnSelect(option.value);
                              setShowOptions(false);
                            }
                          }}
                        >
                          <div className="hcp-select-option-title">
                            {getOptionTitle(option)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )
          : null
      }
    </>
  );
};

HCPSelect.propTypes = {
  initValue: PropTypes.oneOfType([
    PropTypes.string,
    // e.g. ecog for "old" diseases is number
    PropTypes.number,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
  })).isRequired,
  onselect: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  showAutofill: PropTypes.bool,
  renderOnSelect: PropTypes.bool,
  isMulti: PropTypes.bool,
  defaultOpen: PropTypes.bool,
  useTags: PropTypes.bool,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  icon: PropTypes.string,
};

export default HCPSelect;
